import React, { useState, useMemo,useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import DatePicker, { utils } from '@amir04lm26/react-modern-calendar-date-picker';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import jalaali from 'jalaali-js';
import './SidebarForms.css';

// ==========================================================
// #1: کامپوننت‌های مربوط به تحقیق کلمات کلیدی
// ==========================================================

// Helper
const getKeywordDifficultyColor = (difficulty) => {
  if (difficulty <= 30) return '#dcfce7';
  if (difficulty <= 60) return '#fef9c3';
  return '#fee2e2';
};

// Item
function SortableKeywordItem({ item, handleRemoveItem, isExpanded, onToggleDescription, onDescriptionChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 'auto' };
  const rank = item.current_rank ?? 0;
  const volume = item.search_volume ?? 0;
  return (
    <li ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''}>
      <div className="item-content" style={{ background: getKeywordDifficultyColor(item.difficulty ?? 0) }}>
        <span className="drag-handle" {...attributes} {...listeners}>⠿</span>
        <div className="keyword-main-info">
          <span className="keyword-text">{item.text}</span>
          <div className="keyword-metrics">
            <span>سختی: <strong>{item.difficulty ?? 0}</strong></span>
            <span>رتبه: <strong>{rank}</strong></span>
            <span>جستجو: <strong>{volume.toLocaleString()}</strong></span>
          </div>
        </div>
        <div className="item-actions">
          <button onClick={() => onToggleDescription(item.id)} className="description-toggle-btn">{isExpanded ? 'بستن' : 'توضیحات'}</button>
          <button onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id); }} className="remove-btn">&times;</button>
        </div>
      </div>
      {isExpanded && (<div className="description-container"><textarea className="description-textarea" placeholder="توضیحات..." value={item.description} onChange={(e) => onDescriptionChange(item.id, e.target.value)} onClick={(e) => e.stopPropagation()} /></div>)}
    </li>
  );
}

// Form
const KeywordForm = ({ activeNode, updateNodeData }) => {
  const [form, setForm] = useState({ text: '', difficulty: '', current_rank: '', search_volume: '' });
  const [expandedKeywordId, setExpandedKeywordId] = useState(null);
  const nodeDataArray = activeNode.data.keywords || [];
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const handleAddItem = (e) => { e.preventDefault(); if (!form.text.trim()) return; const newItem = { id: Date.now(), text: form.text, difficulty: parseInt(form.difficulty, 10) || 0, current_rank: parseInt(form.current_rank, 10) || 0, search_volume: parseInt(form.search_volume, 10) || 0, description: '' }; updateNodeData(activeNode.id, 'keywords', [...nodeDataArray, newItem]); setForm({ text: '', difficulty: '', current_rank: '', search_volume: '' }); };
  const handleRemoveItem = (itemId) => updateNodeData(activeNode.id, 'keywords', nodeDataArray.filter(item => item.id !== itemId));
  const handleToggleDescription = (keywordId) => setExpandedKeywordId(prevId => (prevId === keywordId ? null : keywordId));
  const handleDescriptionChange = (keywordId, newDescription) => updateNodeData(activeNode.id, 'keywords', nodeDataArray.map(kw => kw.id === keywordId ? { ...kw, description: newDescription } : kw));
  function handleDragEnd(event) { const { active, over } = event; if (active.id !== over.id) { const oldIndex = nodeDataArray.findIndex(item => item.id === active.id); const newIndex = nodeDataArray.findIndex(item => item.id === over.id); updateNodeData(activeNode.id, 'keywords', arrayMove(nodeDataArray, oldIndex, newIndex)); } }
  return (
    <div className="sidebar-form-container">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={nodeDataArray} strategy={verticalListSortingStrategy}>
          <ul className="keyword-list">{nodeDataArray.map(item => (<SortableKeywordItem key={item.id} item={item} handleRemoveItem={handleRemoveItem} isExpanded={expandedKeywordId === item.id} onToggleDescription={handleToggleDescription} onDescriptionChange={handleDescriptionChange} />))}</ul>
        </SortableContext>
      </DndContext>
      <form onSubmit={handleAddItem} className="keyword-add-form"><input name="text" value={form.text} onChange={(e) => setForm(p => ({...p, text: e.target.value}))} placeholder="کلمه کلیدی" /><input name="difficulty" type="number" value={form.difficulty} onChange={(e) => setForm(p => ({...p, difficulty: e.target.value}))} placeholder="سختی (۰-۱۰۰)" /><input name="current_rank" type="number" value={form.current_rank} onChange={(e) => setForm(p => ({...p, current_rank: e.target.value}))} placeholder="جایگاه فعلی" /><input name="search_volume" type="number" value={form.search_volume} onChange={(e) => setForm(p => ({...p, search_volume: e.target.value}))} placeholder="حجم جستجو" /><button type="submit">افزودن</button></form>
    </div>
  );
};

// ==========================================================
// #2: کامپوننت‌های مربوط به تحلیل رقبا
// ==========================================================

// Repeater
function BacklinkRepeater({ competitor, onUpdateBacklinks }) {
  const [form, setForm] = useState({ site: '', da: '', type: 'Dofollow' });
  const backlinks = competitor.backlinks || [];
  const handleAddBacklink = (e) => { e.preventDefault(); if (!form.site.trim()) return; onUpdateBacklinks([...backlinks, { id: Date.now(), ...form }]); setForm({ site: '', da: '', type: 'Dofollow' }); };
  return (
    <div className="backlink-repeater">
      <ul className="backlinks-list">{backlinks.map(b => (<li key={b.id}><span>{b.site}</span><span>(DA: {b.da})</span><span>{b.type}</span><button onClick={() => onUpdateBacklinks(backlinks.filter(bl => bl.id !== b.id))}>&times;</button></li>))}</ul>
      <form onSubmit={handleAddBacklink} className="backlink-add-form"><input name="site" value={form.site} onChange={(e) => setForm(p => ({ ...p, site: e.target.value }))} placeholder="نام سایت" /><input name="da" type="number" value={form.da} onChange={(e) => setForm(p => ({ ...p, da: e.target.value }))} placeholder="DA" /><select name="type" value={form.type} onChange={(e) => setForm(p => ({ ...p, type: e.target.value }))}><option>Dofollow</option><option>Nofollow</option><option>Guest Post</option><option>UGC</option></select><button type="submit">+</button></form>
    </div>
  );
}

// Item
function SortableCompetitorItem({ item, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [expandedSection, setExpandedSection] = useState(null);
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 'auto' };
  return (
    <li ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''}>
      <div className="item-content"><span className="drag-handle" {...attributes} {...listeners}>⠿</span><div className="competitor-main-info"><a href={item.url} target="_blank" rel="noopener noreferrer">{item.url}</a><div className="competitor-metrics"><span>رتبه: <strong>{item.rank}</strong></span><span>لینک هدف: <strong>{item.target_links}</strong></span></div></div><div className="item-actions"><button onClick={() => setExpandedSection(p => p === 'backlinks' ? null : 'backlinks')} className="action-btn">بک‌لینک‌ها</button><button onClick={() => setExpandedSection(p => p === 'description' ? null : 'description')} className="action-btn">توضیحات</button><button onClick={() => onRemove(item.id)} className="remove-btn">&times;</button></div></div>
      {expandedSection === 'description' && (<div className="description-container"><textarea className="description-textarea" value={item.description} onChange={(e) => onUpdate(item.id, { ...item, description: e.target.value })} /></div>)}
      {expandedSection === 'backlinks' && (<div className="description-container"><BacklinkRepeater competitor={item} onUpdateBacklinks={(newBacklinks) => onUpdate(item.id, { ...item, backlinks: newBacklinks })} /></div>)}
    </li>
  );
}

// Form
const CompetitorForm = ({ activeNode, updateNodeData }) => {
  const [form, setForm] = useState({ url: '', rank: '', target_links: '' });
  const nodeDataArray = activeNode.data.competitors || [];
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const handleAddItem = (e) => { e.preventDefault(); if (!form.url.trim()) return; updateNodeData(activeNode.id, 'competitors', [...nodeDataArray, { id: Date.now(), ...form, description: '', backlinks: [] }]); setForm({ url: '', rank: '', target_links: '' }); };
  function handleDragEnd(event) { const { active, over } = event; if (active.id !== over.id) { const oldIndex = nodeDataArray.findIndex(i => i.id === active.id); const newIndex = nodeDataArray.findIndex(i => i.id === over.id); updateNodeData(activeNode.id, 'competitors', arrayMove(nodeDataArray, oldIndex, newIndex)); } }
  return (
    <div className="sidebar-form-container">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}><SortableContext items={nodeDataArray} strategy={verticalListSortingStrategy}><ul className="competitor-list">{nodeDataArray.map(item => (<SortableCompetitorItem key={item.id} item={item} onUpdate={(itemId, updatedItem) => updateNodeData(activeNode.id, 'competitors', nodeDataArray.map(i => i.id === itemId ? updatedItem : i))} onRemove={(itemId) => updateNodeData(activeNode.id, 'competitors', nodeDataArray.filter(i => i.id !== itemId))} />))}</ul></SortableContext></DndContext>
      <form onSubmit={handleAddItem} className="competitor-add-form"><input name="url" value={form.url} onChange={(e) => setForm(p => ({...p, url: e.target.value}))} placeholder="URL رقیب" /><input name="rank" type="number" value={form.rank} onChange={(e) => setForm(p => ({...p, rank: e.target.value}))} placeholder="رتبه" /><input name="target_links" type="number" value={form.target_links} onChange={(e) => setForm(p => ({...p, target_links: e.target.value}))} placeholder="لینک هدف" /><button type="submit">افزودن رقیب</button></form>
    </div>
  );
};

// ==========================================================
// #3: کامپوننت‌های مربوط به استراتژی محتوا
// ==========================================================

// Toolbar
const MenuBar = ({ editor }) => {
  if (!editor) return null;
  return (
    <div className="editor-menu-bar">
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''}><strong>B</strong></button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''}><em>I</em></button>
      <button type="button" onClick={() => editor.chain().focus().setParagraph().run()} className={editor.isActive('paragraph') ? 'is-active' : ''}>پاراگراف</button>
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}>H1</button>
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}>H2</button>
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}>H3</button>
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''}>لیست</button>
    </div>
  );
};

// Form
const ContentStrategyForm = ({ activeNode, updateNodeData }) => {
  const editor = useEditor({ extensions: [StarterKit], content: activeNode.data.content || '', onUpdate: ({ editor }) => updateNodeData(activeNode.id, 'content', editor.getHTML()) });
  React.useEffect(() => { if (editor && !editor.isDestroyed && editor.getHTML() !== activeNode.data.content) { editor.commands.setContent(activeNode.data.content || ''); } }, [activeNode, editor]);
  return (
    <div className="sidebar-form-container text-editor-wrapper">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="editor-content" />
    </div>
  );
};

// ==========================================================
// #4: کامپوننت‌های مربوط به لینک‌سازی
// ==========================================================


const LinkBuildingForm = ({ activeNode, updateNodeData, globalLinkStats }) => {
  const [linkType, setLinkType] = useState('backlink');
  const [backlinkForm, setBacklinkForm] = useState({
    site_name: '',
    target_keyword: '',
    placement: '',
    dateRange: { from: null, to: null },
    cost: ''
  });
  const [reportageForm, setReportageForm] = useState({
    site_name: '',
    target_keyword: '',
    link_address: '',
    total_cost: '',
    keyword_count: ''
  });

  const nodeDataArray = activeNode.data.links || [];

  // 🔹 تابع تبدیل تاریخ شمسی به میلادی Date
  const jalaliToGregorianDate = (jalaliObj) => {
    if (!jalaliObj) return null;
    const { gy, gm, gd } = jalaali.toGregorian(
      jalaliObj.year,
      jalaliObj.month,
      jalaliObj.day
    );
    return new Date(Date.UTC(gy, gm - 1, gd)); // استفاده از UTC برای دقت بالا
  };

  // 🔹 محاسبه تعداد روزهای بین دو تاریخ شمسی
  const calculateDuration = (range) => {
    if (!range?.from || !range?.to) return 0;
    const from = jalaliToGregorianDate(range.from);
    const to = jalaliToGregorianDate(range.to);
    const diff = Math.floor((to - from) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  };
  const today = jalaali.toJalaali(new Date());
  const todayNumeric = today.jy * 10000 + today.jm * 100 + today.jd;
  const formatDateObject = (date) =>
    date ? `${date.year}/${date.month}/${date.day}` : null;

  const handleAddItem = useCallback(
    (e) => {
      e.preventDefault();
      let newItem;

      if (linkType === 'backlink') {
        if (!backlinkForm.site_name) return;

        const durationDays = calculateDuration(backlinkForm.dateRange);
        newItem = {
          id: Date.now(),
          type: 'backlink',
          site_name: backlinkForm.site_name,
          target_keyword: backlinkForm.target_keyword,
          placement: backlinkForm.placement,
          cost: parseInt(backlinkForm.cost, 10) || 0,
          start_date: formatDateObject(backlinkForm.dateRange?.from),
          end_date: formatDateObject(backlinkForm.dateRange?.to),
          duration:
            durationDays > 0 ? `${durationDays} روز` : 'دائمی',
        };

        setBacklinkForm({
          site_name: '',
          target_keyword: '',
          placement: '',
          dateRange: { from: null, to: null },
          cost: '',
        });
      } else {
        if (!reportageForm.site_name) return;
        newItem = {
          id: Date.now(),
          type: 'reportage',
          site_name: reportageForm.site_name,
          target_keyword: reportageForm.target_keyword,
          link_address: reportageForm.link_address,
          total_cost: parseInt(reportageForm.total_cost, 10) || 0,
          keyword_count: parseInt(reportageForm.keyword_count, 10) || 0,
        };
        setReportageForm({
          site_name: '',
          target_keyword: '',
          link_address: '',
          total_cost: '',
          keyword_count: '',
        });
      }

      updateNodeData(activeNode.id, 'links', [...nodeDataArray, newItem]);
    },
    [linkType, backlinkForm, reportageForm, nodeDataArray, updateNodeData, activeNode.id]
  );

  const pageCost = useMemo(() => {
    return nodeDataArray.reduce((sum, link) => {
      if (link.type === 'backlink') {
        return sum + (Number(link.cost) || 0);
      }
      if (link.type === 'reportage') {
        const costPerKeyword =
          (Number(link.total_cost) || 0) / (Number(link.keyword_count) || 1);
        return sum + costPerKeyword;
      }
      return sum;
    }, 0);
  }, [nodeDataArray]);

  const durationDisplay = useMemo(
    () => calculateDuration(backlinkForm.dateRange),
    [backlinkForm.dateRange]
  );

  return (
    <div className="sidebar-form-container">
      {/* آمار کلی پروژه */}
      <div className="global-stats-summary">
        <div className="stat-item">
          <span>هزینه کل بک‌لینک‌ها:</span>
          <strong>
            {(globalLinkStats?.totalBacklinkCost || 0).toLocaleString()} تومان
          </strong>
        </div>
        <div className="stat-item">
          <span>هزینه کل رپورتاژها:</span>
          <strong>
            {(globalLinkStats?.totalReportageCost || 0).toLocaleString()} تومان
          </strong>
        </div>
      </div>

      <div className="total-cost-summary">
        <span>هزینه این صفحه:</span>
        <strong>{Math.round(pageCost).toLocaleString()} تومان</strong>
      </div>

      {/* لیست لینک‌ها */}
      <ul className="links-list">
  {nodeDataArray.map((link) => {
    // --- شروع بخش جدید: محاسبه وضعیت برای هر لینک ---
    let status = '';
    let statusClassName = '';

    // این منطق فقط برای بک‌لینک‌ها اجرا می‌شود
    if (link.type === 'backlink') {
      if (link.end_date) {
        // تاریخ امروز را به شمسی دریافت می‌کنیم
        const todayJalaali = jalaali.toJalaali(new Date());
        const todayNumeric = todayJalaali.jy * 10000 + todayJalaali.jm * 100 + todayJalaali.jd;

        // تاریخ پایان لینک را از رشته به عدد تبدیل می‌کنیم
        const [endYear, endMonth, endDay] = link.end_date.split('/').map(Number);
        const endDateNumeric = endYear * 10000 + endMonth * 100 + endDay;

        // مقایسه تاریخ‌ها
        if (todayNumeric > endDateNumeric) {
          status = 'منقضی شده';
          statusClassName = 'status-expired';
        } else {
          status = 'فعال';
          statusClassName = 'status-active';
        }
      } else {
        // اگر تاریخ پایانی وجود نداشت، لینک فعال و دائمی است
        status = 'فعال';
        statusClassName = 'status-active';
      }
    }
    // --- پایان بخش جدید ---

    // محاسبات هزینه (بدون تغییر)
    const totalCost = Number(link.total_cost) || 0;
    const keywordCount = Number(link.keyword_count) || 0;
    const costPerKeyword = (totalCost > 0 && keywordCount > 0) ? Math.round(totalCost / keywordCount) : 0;
    
    return (
      <li
        key={link.id}
        className={link.type === 'backlink' ? 'backlink-item' : 'reportage-item'}
      >
        <div className="link-item-header">
          <strong>{link.site_name}</strong>

          {/* --- شروع تغییر اصلی: نمایش شرطی بج وضعیت --- */}
          {status && (
            <span className={`status-badge ${statusClassName}`}>
              {status}
            </span>
          )}
          {/* --- پایان تغییر اصلی --- */}

          <span className="link-type-badge">
            {link.type === 'backlink' ? 'بک‌لینک' : 'رپورتاژ'}
          </span>
          <button
            onClick={() =>
              updateNodeData(
                activeNode.id,
                'links',
                nodeDataArray.filter((l) => l.id !== link.id)
              )
            }
            className="remove-btn"
          >
            &times;
          </button>
        </div>
        {link.type === 'backlink' ? (
          <div className="link-item-details">
            <span>کلمه هدف: {link.target_keyword || '-'}</span>
            <span>محل: {link.placement}</span>
            <span>مدت: {link.duration}</span>
            <span>شروع: {link.start_date || '-'}</span>
            <span>پایان: {link.end_date || '-'}</span>
            <span>هزینه: {(Number(link.cost) || 0).toLocaleString()} ت</span>
          </div>
        ) : (
          <div className="link-item-details">
            <a
              href={link.link_address}
              target="_blank"
              rel="noopener noreferrer"
            >
              مشاهده لینک
            </a>
            <span>کلمه هدف: {link.target_keyword || '-'}</span>
            <span>
              مبلغ کل: {totalCost.toLocaleString()} ت
            </span>
            <span>
              هزینه/کلمه:{' '}
              {costPerKeyword > 0 ? costPerKeyword.toLocaleString() : '-'}{' '}
              ت
            </span>
          </div>
        )}
      </li>
    );
  })}
</ul>

      {/* فرم افزودن */}
      <form onSubmit={handleAddItem} className="link-add-form">
        <div className="link-type-selector">
          <button
            type="button"
            onClick={() => setLinkType('backlink')}
            className={linkType === 'backlink' ? 'active' : ''}
          >
            بک‌لینک
          </button>
          <button
            type="button"
            onClick={() => setLinkType('reportage')}
            className={linkType === 'reportage' ? 'active' : ''}
          >
            رپورتاژ
          </button>
        </div>

        {/* فرم بک‌لینک */}
        {linkType === 'backlink' && (
          <div className="form-fields">
            <input
              value={backlinkForm.site_name}
              onChange={(e) =>
                setBacklinkForm((p) => ({ ...p, site_name: e.target.value }))
              }
              placeholder="نام سایت"
            />
            <input
              value={backlinkForm.target_keyword}
              onChange={(e) =>
                setBacklinkForm((p) => ({ ...p, target_keyword: e.target.value }))
              }
              placeholder="کلمه کلیدی هدف"
            />
            <input
              value={backlinkForm.placement}
              onChange={(e) =>
                setBacklinkForm((p) => ({ ...p, placement: e.target.value }))
              }
              placeholder="محل قرارگیری"
            />
            <input
              type="number"
              value={backlinkForm.cost}
              onChange={(e) =>
                setBacklinkForm((p) => ({ ...p, cost: e.target.value }))
              }
              placeholder="هزینه (تومان)"
            />

            <div className="date-picker-wrapper">
              <DatePicker
                value={backlinkForm.dateRange}
                onChange={(range) =>
                  setBacklinkForm((p) => ({ ...p, dateRange: range }))
                }
                inputPlaceholder="انتخاب بازه زمانی"
                shouldHighlightWeekends
                locale="fa"
                isRangePicker
              />
            </div>

            {durationDisplay > 0 && (
              <div className="duration-display">
                <span>
                  مدت زمان انتخاب‌شده: <strong>{durationDisplay} روز</strong>
                </span>
              </div>
            )}
          </div>
        )}

        {/* فرم رپورتاژ */}
        {linkType === 'reportage' && (
          <div className="form-fields">
            <input
              value={reportageForm.site_name}
              onChange={(e) =>
                setReportageForm((p) => ({ ...p, site_name: e.target.value }))
              }
              placeholder="نام سایت"
            />
            <input
              value={reportageForm.target_keyword}
              onChange={(e) =>
                setReportageForm((p) => ({ ...p, target_keyword: e.target.value }))
              }
              placeholder="کلمه کلیدی هدف"
            />
            <input
              value={reportageForm.link_address}
              onChange={(e) =>
                setReportageForm((p) => ({ ...p, link_address: e.target.value }))
              }
              placeholder="آدرس لینک رپورتاژ"
            />
            <input
              type="number"
              value={reportageForm.total_cost}
              onChange={(e) =>
                setReportageForm((p) => ({ ...p, total_cost: e.target.value }))
              }
              placeholder="مبلغ کل (تومان)"
            />
            <input
              type="number"
              value={reportageForm.keyword_count}
              onChange={(e) =>
                setReportageForm((p) => ({ ...p, keyword_count: e.target.value }))
              }
              placeholder="تعداد کلمات کلیدی"
            />
          </div>
        )}

        <button type="submit">افزودن لینک</button>
      </form>
    </div>
  );
};
// ==========================================================
// #5: کامپوننت اصلی برای سوییچ بین فرم‌ها
// ==========================================================
const SidebarForms = ({ activeNode, menuType, updateNodeData, globalLinkStats }) => {
  if (!activeNode) {
    return <p className="empty-message">صفحه‌ای انتخاب نشده است.</p>;
  }

  // View
  return (
    <>
      <div style={{ display: menuType === 'keywords' ? 'flex' : 'none', height: '100%' }}>
        <KeywordForm activeNode={activeNode} updateNodeData={updateNodeData} />
      </div>

      <div style={{ display: menuType === 'competitors' ? 'flex' : 'none', height: '100%' }}>
        <CompetitorForm activeNode={activeNode} updateNodeData={updateNodeData} />
      </div>

      <div style={{ display: menuType === 'content' ? 'flex' : 'none', height: '100%' }}>
        <ContentStrategyForm activeNode={activeNode} updateNodeData={updateNodeData} />
      </div>

      <div style={{ display: menuType === 'links' ? 'flex' : 'none', height: '100%' }}>
        <LinkBuildingForm activeNode={activeNode} updateNodeData={updateNodeData} globalLinkStats={globalLinkStats} />
      </div>
    </>
  );
};

export default SidebarForms;